import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useCachedState } from '../../../hooks/useCachedState';
import { writeInstanceIdsParam } from '../../../hooks/useChatUrlInstanceIds';
import type { ControlSelections } from '../../types/controls';
import {
  type AppliedDashboardFilters,
  CONTROL_PARAM_PREFIX,
  markApplied,
  replaceFilterParams,
  TIME_RANGE_PARAM,
  withoutInstanceScope,
  writeControlParam,
} from '../applied/filterParams';
import type { TemplateControls } from '../templateControls';
import {
  filterMemoryKey,
  filtersToRemember,
  missingControlSelections,
  missingInstanceScope,
  NOTHING_REMEMBERED,
  type RememberedFilters,
  rememberedSelections,
} from './filterMemory';

type FilterMemory = {
  rememberFilters: (selections: ControlSelections, timeRange: string | undefined) => void;
};

/** Answers a link's proposal with its own filters, then fills what the URL still lacks. Only the
 * answer is decided on arrival: an edit looks like a proposal, and must not be swallowed. */
export const useFilterMemory = ({
  templateId,
  template,
  applied,
  templateDefaultTimeRange,
  accessibleInstanceIds,
  ownsTheRoute,
}: {
  templateId: string | undefined;
  template: TemplateControls;
  applied: AppliedDashboardFilters;
  templateDefaultTimeRange: string | undefined;
  accessibleInstanceIds: string[];
  ownsTheRoute: boolean;
}): FilterMemory => {
  const [, setSearchParams] = useSearchParams();
  const key = useMemo(() => filterMemoryKey(templateId), [templateId]);
  const [remembered, updateRemembered] = useCachedState<RememberedFilters>(key, NOTHING_REMEMBERED);
  const { instanceControlKey, controlKeys } = template;

  const appliedControlParams = useMemo(
    () => withoutInstanceScope(applied.selectionsByControlKey, instanceControlKey),
    [applied.selectionsByControlKey, instanceControlKey],
  );

  // A link, or a shared URL, can name a control this template doesn't have.
  const paramsOfForeignControls = useMemo(
    () =>
      controlKeys.length === 0
        ? []
        : Object.keys(appliedControlParams).filter((controlKey) => !controlKeys.includes(controlKey)),
    [appliedControlParams, controlKeys],
  );

  const answeredFor = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!ownsTheRoute || controlKeys.length === 0) return;
    const arriving = answeredFor.current !== templateId;
    answeredFor.current = templateId;

    if (arriving && !applied.isApplied && Object.keys(remembered).length > 0) {
      const selections = rememberedSelections(remembered, template);
      setSearchParams(
        (prev) =>
          markApplied(
            replaceFilterParams(new URLSearchParams(prev), selections, remembered.timeRangeOverride, template),
          ),
        { replace: true },
      );
      return;
    }

    // Gaps only, so it settles in one pass and never disturbs a proposal or an edit.
    // Memory fills them on arrival alone: once the bar is in use an emptied control is a
    // deliberate edit, and a gap it left is indistinguishable from one never filled.
    const memory = arriving ? remembered : NOTHING_REMEMBERED;
    const timeRange = applied.timeRangeOverride ? undefined : (memory.timeRangeOverride ?? templateDefaultTimeRange);
    const instanceIds = missingInstanceScope({
      applied,
      remembered: memory,
      instanceControlKey,
      accessibleInstanceIds,
    });
    const selections = missingControlSelections({ appliedControlParams, remembered: memory, controlKeys });

    if (!timeRange && !instanceIds?.length && selections.length === 0 && paramsOfForeignControls.length === 0) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (timeRange) next.set(TIME_RANGE_PARAM, timeRange);
        if (instanceIds?.length) writeInstanceIdsParam(next, instanceIds);
        for (const [controlKey, values] of selections) {
          writeControlParam(next, controlKey, values);
        }
        for (const controlKey of paramsOfForeignControls) next.delete(CONTROL_PARAM_PREFIX + controlKey);
        return next;
      },
      { replace: true },
    );
  }, [
    ownsTheRoute,
    templateId,
    applied,
    appliedControlParams,
    remembered,
    template,
    templateDefaultTimeRange,
    instanceControlKey,
    accessibleInstanceIds,
    controlKeys,
    paramsOfForeignControls,
    setSearchParams,
  ]);

  const rememberFilters = useCallback(
    (selections: ControlSelections, timeRange: string | undefined) =>
      updateRemembered(() => filtersToRemember(selections, timeRange, template)),
    [updateRemembered, template],
  );

  return { rememberFilters };
};
